import React,{useState} from "react";
import {registerBlockType} from "@wordpress/blocks";
import { Fragment } from "@wordpress/element";
import IconSelect from "./IconSelect";
import {RichText} from "@wordpress/block-editor";
import {PanelBody,Panel,RangeControl,SelectControl,CheckboxControl,TextControl,Button} from "@wordpress/components";
import {
    useBlockProps,
    ColorPalette,
    InspectorControls,
    InnerBlocks,
    BlockControls,
    AlignmentToolbar,
    MediaUpload, 
    MediaUploadCheck,
    __experimentalLinkControl as LinkControl
} from '@wordpress/block-editor';

registerBlockType("template-scripts/front-slider",{
  "title":"Front Slider",
  "category":"template-scripts",
  "icon":"button",
  attributes:{
    speed:{type:'string',default:'1000'},
    duration:{type:'string',default:'4000'},
    tagline:{type:'string',default:''},
    buttonLink:{type:'object',default:{}},
    buttonText:{type:'string',default:''},
    taglineSupporting:{type:'string',default:''},
    taglineSupportingWidth:{type:'number',default:'500'},
    slideAnimation:{type:'boolean',default:false},
    sliderStyle:{type:'string',default:'default'},
    slides:{type:'array',default:[{
      headline:"",
      text:"",
      buttonText:"",
      buttonLink:"",
      icon:"",
      image: null,
      mobileImage: null
    }]}

  },

  edit:function({attributes,setAttributes}){
    console.log('Edit - Current slides:', attributes.slides);
    const sliderContainer = React.useRef();
    const sliderRef = React.useRef();
    const [slideIndex,setSlideIndex] = useState(0);
    const [expandedSlide, setExpandedSlide] = useState(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    React.useEffect(()=>{
      if(!sliderContainer.current) return;
      if(sliderRef.current){
       sliderRef.current.refresh();
      }
      else{
        sliderRef.current = new window.frontSlider(sliderContainer.current,{
          duration:attributes.duration,
          speed:attributes.speed,
          editor:true,
          devMode:true
        });
      }
      setSlideIndex(sliderRef.current.slideIndex);
      

    },[attributes.slides.length]);
   
    const updateSlide = (index, key, value) => {
      const newSlideDetails = attributes.slides.map((slide, i) => 
          i === index ? { ...slide, [key]: value } : slide
      );
      setAttributes({ slides: newSlideDetails });
  };

  const addSlide = () => {
      setSlideIndex(attributes.slides.length);
      setAttributes({
          slides: [...attributes.slides, {
              headline: "",
              text: "",
              buttonText: "",
              buttonLink: "",
              icon: "",
              image: null,
              mobileImage: null
          }]
      });
  };


  const deleteSlide = (index) => {
    const newSlides = attributes.slides.filter((_, i) => i !== index);
    setAttributes({ slides: newSlides });
    // Reset expanded slide if the deleted slide was expanded
    if (expandedSlide === index) {
      setExpandedSlide(null);
    } else if (expandedSlide > index) {
      // Adjust expanded slide index if a slide before it was deleted
      setExpandedSlide(expandedSlide - 1);
    }
  };

  const reorderSlides = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newSlides = [...attributes.slides];
    const [movedSlide] = newSlides.splice(fromIndex, 1);
    newSlides.splice(toIndex, 0, movedSlide);
    
    setAttributes({ slides: newSlides });
    
    // Update expanded slide index if needed
    if (expandedSlide === fromIndex) {
      setExpandedSlide(toIndex);
    } else if (expandedSlide === toIndex) {
      setExpandedSlide(fromIndex < toIndex ? expandedSlide - 1 : expandedSlide + 1);
    } else if (expandedSlide > fromIndex && expandedSlide <= toIndex) {
      setExpandedSlide(expandedSlide - 1);
    } else if (expandedSlide < fromIndex && expandedSlide >= toIndex) {
      setExpandedSlide(expandedSlide + 1);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    // Store the dragged element reference
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      reorderSlides(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const slideEditorComponents = attributes.slides.map((s,i)=>{
    const isExpanded = expandedSlide === i;
    const captionPreview = s.imageCaption || s.imageCaptionSmall || "No caption";
    const hasImage = s.image?.url;
    const isDragging = draggedIndex === i;
    const isDragOver = dragOverIndex === i;
    
    return (
      <div 
        key={`slide${i}`}
        draggable={!isExpanded}
        onDragStart={(e) => {
          // Prevent dragging if clicking on expanded content
          if (e.target.closest('.expanded-content')) {
            e.preventDefault();
            return;
          }
          handleDragStart(e, i);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          handleDragOver(e, i);
        }}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, i)}
        onDragEnd={handleDragEnd}
        style={{
          border: isDragOver ? "2px solid #2271b1" : "1px solid #ddd",
          borderRadius: "4px",
          marginBottom: "8px",
          backgroundColor: isDragging ? "#f0f0f0" : isDragOver ? "#e7f3ff" : "#fff",
          opacity: isDragging ? 0.5 : 1,
          cursor: isDragging ? "grabbing" : (!isExpanded ? "move" : "default"),
          transition: "all 0.2s ease"
        }}
      >
        {/* Collapsed Header - Clickable */}
        <div
          onClick={(e) => {
            // Don't expand if clicking on drag handle
            if (e.target.closest('.drag-handle')) return;
            setExpandedSlide(isExpanded ? null : i);
          }}
          style={{
            padding: "12px 16px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: isExpanded ? "#f0f0f0" : "transparent",
            borderBottom: isExpanded ? "1px solid #ddd" : "none",
            userSelect: "none"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
            {/* Drag Handle */}
            <div 
              className="drag-handle"
              style={{
                cursor: "grab",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                color: "#999",
                fontSize: "14px",
                userSelect: "none"
              }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Drag to reorder"
            >
              <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" style={{ display: "block" }}>
                <circle cx="2" cy="3" r="1.5"/>
                <circle cx="6" cy="3" r="1.5"/>
                <circle cx="10" cy="3" r="1.5"/>
                <circle cx="2" cy="8" r="1.5"/>
                <circle cx="6" cy="8" r="1.5"/>
                <circle cx="10" cy="8" r="1.5"/>
                <circle cx="2" cy="13" r="1.5"/>
                <circle cx="6" cy="13" r="1.5"/>
                <circle cx="10" cy="13" r="1.5"/>
              </svg>
            </div>
            {hasImage && (
              <img 
                src={s.image.url} 
                style={{
                  width: "40px",
                  height: "40px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: "1px solid #ddd"
                }}
                alt={`Slide ${i+1} preview`}
              />
            )}
            <div>
              <strong style={{ display: "block", marginBottom: "2px" }}>
                Slide {i+1}
              </strong>
              <span style={{ fontSize: "12px", color: "#666" }}>
                {captionPreview.length > 50 ? captionPreview.substring(0, 50) + "..." : captionPreview}
              </span>
            </div>
          </div>
          <span style={{ fontSize: "18px", color: "#666" }}>
            {isExpanded ? "−" : "+"}
          </span>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="expanded-content" style={{ padding: "16px" }}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                Image
              </label>
              <MediaUpload
                value={s.image}
                onSelect={(v)=>updateSlide(i,"image",v)}
                render={({open})=>(
                  <div>
                    {hasImage ? (
                      <img 
                        src={s.image.url} 
                        style={{
                          width: "100%",
                          maxWidth: "200px",
                          height: "auto",
                          borderRadius: "4px",
                          marginBottom: "8px",
                          border: "1px solid #ddd"
                        }}
                        alt="Slide image"
                      />
                    ) : (
                      <div style={{
                        width: "200px",
                        height: "120px",
                        backgroundColor: "#f5f5f5",
                        border: "1px dashed #ddd",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#999",
                        marginBottom: "8px"
                      }}>
                        No image
                      </div>
                    )}
                    <Button onClick={open} variant="secondary">
                      {hasImage ? "Change Image" : "Select Image"}
                    </Button>
                  </div>
                )}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                Icon
              </label>
              <IconSelect onChange={(icon)=>updateSlide(i,"icon",icon)} value={s.icon}/>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                Sub Caption (Small Text)
              </label>
              <TextControl 
                placeholder="Image Caption Small Text" 
                value={s.imageCaptionSmall || ""} 
                onChange={(v)=>updateSlide(i,"imageCaptionSmall",v)}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                Caption
              </label>
              <TextControl 
                placeholder="Image Caption" 
                value={s.imageCaption || ""} 
                onChange={(v)=>updateSlide(i,"imageCaption",v)}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "600" }}>
                Caption Position
              </label>
              <SelectControl 
                value={s.imageCaptionPosition || "bottom-center"} 
                onChange={(v)=>updateSlide(i,"imageCaptionPosition",v)}
                options={[
                  { label: "Top Left", value: "top-left" },
                  { label: "Top Center", value: "top-center" },
                  { label: "Top Right", value: "top-right" },
                  { label: "Middle Left", value: "middle-left" },
                  { label: "Middle Center", value: "middle-center" },
                  { label: "Middle Right", value: "middle-right" },
                  { label: "Bottom Left", value: "bottom-left" },
                  { label: "Bottom Center", value: "bottom-center" },
                  { label: "Bottom Right", value: "bottom-right" }
                ]}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center", marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #ddd" }}>
              <Button 
                onClick={()=>deleteSlide(i)} 
                isDestructive={true}
                variant="secondary"
              >
                Delete Slide
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  });


    const slideComponents = attributes.slides.map((s,i)=>{

      return (<li key={`slide${i}`}>
        <div className="slide-image-holder">
          <img src={s.image?.url} className="slide-image"/>
          <div className={`slide-image-caption ${s.imageCaptionPosition}`}>
            <div className="caption-text-container">
              <RichText placeholder="Image Caption Small Text" value={s.imageCaptionSmall} tagName="span" className="imageCaptionSmall" onChange={(v)=>updateSlide(i,"imageCaptionSmall",v)}/>
              <RichText placeholder="Image Caption" value={s.imageCaption} tagName="span" className="imageCaption" onChange={(v)=>updateSlide(i,"imageCaption",v)}/>
            </div>
            {s.icon?.svg ? (<div className="svgObject" dangerouslySetInnerHTML={{__html:s.icon?.svg}}></div>) : (<></>)}
          </div>
        </div>


    </li>);


    });
    


    return (<div>

      <BlockControls/>
      <InspectorControls>
        <Panel>

          <PanelBody title="Slides">
          {slideEditorComponents}
          <div style={{display:"flex",justifyContent:"center"}}>
            <Button onClick={()=>addSlide()} isPrimary={true} style={{marginTop:"10px",backgroundColor:"#198754"}}>Add Slide</Button>
          </div>
          </PanelBody>
          <PanelBody title="Slider Options">
            <RangeControl
              label={`Slider Speed`}
              min={100}
              max={4000}
              step={100}
              value={parseInt(attributes.speed)}
              onChange={(v)=>setAttributes({speed:v})}
            />
            <RangeControl
              label={`Slide Duration`}
              min={1000}
              step={100}
              max={20000}
              value={parseInt(attributes.duration)}
              onChange={(v)=>setAttributes({duration:v})}
            />
            <RangeControl
              label={`Tagline Supporting Width`}
              min={100}
              max={1000}
              step={10}
              value={parseInt(attributes.taglineSupportingWidth)}
              onChange={(v)=>setAttributes({taglineSupportingWidth:v})}
            />

          </PanelBody>
        </Panel>
        
      </InspectorControls>
      <div className="sectionHolder">
        <div className="container" style={{padding:"50px 10px 0px 10px"}}>
          <div className="frontSliderHolder" style={{marginBottom:"0px"}}>
            <ul className={`frontSlider ${attributes.slideAnimation ? "slideIn" : ""} ${attributes.sliderStyle !== 'default' ? attributes.sliderStyle : ''}`} ref={sliderContainer} style={{marginBottom:"0px",aspectRatio:"16/9"}}>
              <div className="btn-arrow btn-previous">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M328 112L184 256l144 144"/></svg>
              </div>

              <div className="btn-arrow btn-next">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M184 112l144 144-144 144"/></svg>
              </div>
              <div className="staticText" style={{maxWidth:attributes.taglineSupportingWidth+"px"}}>
                <RichText tagName="h1" placeholder="Sticky Tagline" value={attributes.tagline} onChange={(v)=>setAttributes({tagline:v})}/>
                <RichText tagName="p" placeholder="Sticky Tagline Supporting Text" value={attributes.taglineSupporting} onChange={(v)=>setAttributes({taglineSupporting:v})}/>
                <br/><RichText className="btn" placeholder="Button Text" value={attributes.buttonText} tagName="div" onChange={(v)=>setAttributes({buttonText:v})}/>
                <div style={{backgroundColor:"#fff",textShadow:"none"}}><LinkControl placeholder="Button Link" onChange={(v)=>{ setAttributes({"buttonLink":v}); }} value={attributes.buttonLink}/></div>
              </div>
              
              {slideComponents}                 
            </ul> 
          </div>
        </div>
      </div>

    </div>);
  },
  save:function({attributes}){
    console.log('Save - Current slides:', attributes.slides);
    console.log("SAVE SLIDES");
    console.log(attributes.slides);
    const bulletComponents = new Array(attributes.slides.length).fill("").map((a,i)=>{
      return (<li key={`slideBullet_${i}`}></li>);
    });

    const slideComponents = attributes.slides.map((a,i)=>{
      console.log(attributes.slides[i].icon);

      return (<li key={`slide${i}`}>
        <div className="slide-image-holder">
          <img src={attributes.slides[i]?.image?.url} data-mobileImage={attributes.slides[i]?.mobileImage?.url || attributes.slides[i]?.image?.url} className="slide-image"/>
          {attributes.slides[i].icon?.svg ? (<div className={`slide-image-caption ${attributes.slides[i]?.imageCaptionPosition}`}>
            <div className="caption-text-container">
              <RichText.Content value={attributes.slides[i]?.imageCaptionSmall} className="imageCaptionSmall" tagName="span"/>
              <RichText.Content value={attributes.slides[i]?.imageCaption} tagName="span"/>
            </div>
            <div className="svgObject" style={{color:attributes.iconColor}} dangerouslySetInnerHTML={{__html:attributes.slides[i].icon?.svg}}></div>
          </div>) : (<></>)}
        </div>
        {(attributes.slides[i].headline || attributes.slides[i].text) ? (<div className="text">
          <RichText.Content value={attributes.slides[i]?.headline} tagName="h3"/>
          <RichText.Content value={attributes.slides[i]?.text} tagName="span"/>
          {attributes.slides[i]?.buttonText ? (<><br/>
            <a href={attributes.slides[i]?.buttonLink?.url} className="btn"><RichText.Content value={attributes.slides[i]?.buttonText} tagName="span"/></a>
          </>) : (<></>)}
        </div>) : (<></>)}
      </li>);

    });

    return (
      <div 
        className="sectionHolder"
        data-slides={JSON.stringify(attributes.slides)}
      >
        <div className="container">
            <div className="frontSliderHolder">
              <ul className={`frontSlider ${attributes.slideAnimation ? "slideIn" : ""} ${attributes.sliderStyle !== 'default' ? attributes.sliderStyle : ''} front-slider-live`} data-duration={attributes.duration} data-speed={attributes.speed}>
                <div className="btn-arrow btn-previous">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M328 112L184 256l144 144"/></svg>
                </div>

                <div className="btn-arrow btn-next">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="48" d="M184 112l144 144-144 144"/></svg>
                </div>
                <div className="staticText" style={{maxWidth:attributes.taglineSupportingWidth+"px"}}>
                  <RichText.Content tagName="h1" value={attributes.tagline}/>
                  <RichText.Content tagName="p" value={attributes.taglineSupporting}/>
                  <a href={attributes.buttonLink?.url} className="btn"><RichText.Content tagName="div" value={attributes.buttonText}/></a>
                </div>
                
                {slideComponents}                 
              </ul> 
              <ul className="frontSliderBullets">
                {bulletComponents}
              </ul>
          </div>
        </div>
      </div>
      );
  }
});

